import { useCallback, useEffect, useMemo, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Invoice, InvoiceSource } from "@common/types/invoice";
import { useBulkDownloadInvoicePdf } from "@api/invoice.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";

const COOLDOWN_MS = 15_000;
const LAST_INVOICES_PDF_DOWNLOAD_KEY = "lastInvoicesPdfDownload";
const PROCESSING_MESSAGE =
    "Your file is processing. You'll receive an email when it's ready.";
const KICK_ONLY_MESSAGE =
    "Only invoices created in Kick can be downloaded as a PDF";

interface UseInvoicesBulkDownloadPdfParams {
    selectedInvoices: Invoice[];
}

function isNativeInvoice(invoice: Invoice): boolean {
    return invoice.source === InvoiceSource.MANUAL;
}

function readCooldownUntil(): number | null {
    const raw = localStorage.getItem(LAST_INVOICES_PDF_DOWNLOAD_KEY);
    if (!raw) {
        return null;
    }
    const startedAt = Number.parseInt(raw, 10);
    if (Number.isNaN(startedAt)) {
        return null;
    }
    const until = startedAt + COOLDOWN_MS;
    return until > Date.now() ? until : null;
}

// Queues one zip of the selected Kick invoices, the same way the documents
// page bulk download does. The job renders any PDF that is not archived yet
// (including drafts) and emails a link. Synced invoices have no Kick PDF.
export function useInvoicesBulkDownloadPdf({
    selectedInvoices,
}: UseInvoicesBulkDownloadPdfParams) {
    const { toast } = useToaster();
    const { workspaceId } = useScope();
    const downloadInvoicePdf = useBulkDownloadInvoicePdf();
    const [cooldownUntil, setCooldownUntil] = useState(readCooldownUntil);

    useEffect(() => {
        if (cooldownUntil === null) {
            return undefined;
        }
        const remainingMs = cooldownUntil - Date.now();
        if (remainingMs <= 0) {
            setCooldownUntil(null);
            return undefined;
        }
        const timer = setTimeout(() => setCooldownUntil(null), remainingMs);
        return () => clearTimeout(timer);
    }, [cooldownUntil]);

    const nativeInvoices = useMemo(
        () => selectedInvoices.filter(isNativeInvoice),
        [selectedInvoices],
    );
    const isCoolingDown = cooldownUntil !== null;

    const handleDownload = useCallback(async () => {
        if (!workspaceId || nativeInvoices.length === 0 || isCoolingDown) {
            return;
        }
        try {
            await downloadInvoicePdf.mutateAsync({
                workspaceId,
                invoiceIds: nativeInvoices.map((invoice) => invoice.id),
            });
        } catch {
            toast("Couldn't download the PDFs", undefined, {
                variant: "error",
            });
            return;
        }

        const startedAt = Date.now();
        localStorage.setItem(
            LAST_INVOICES_PDF_DOWNLOAD_KEY,
            startedAt.toString(),
        );
        setCooldownUntil(startedAt + COOLDOWN_MS);

        const skippedCount = selectedInvoices.length - nativeInvoices.length;
        const skippedVerb = skippedCount === 1 ? "wasn't" : "weren't";
        const skippedNote =
            skippedCount > 0
                ? ` Skipped ${pluralizeWithCount("invoice", skippedCount)} that ${skippedVerb} created in Kick.`
                : "";
        toast(`${PROCESSING_MESSAGE}${skippedNote}`);
    }, [
        workspaceId,
        nativeInvoices,
        isCoolingDown,
        selectedInvoices.length,
        downloadInvoicePdf,
        toast,
    ]);

    return {
        handleDownload,
        isDownloading: downloadInvoicePdf.isPending,
        allowDownload: nativeInvoices.length > 0 && !isCoolingDown,
        downloadDisabledTooltip:
            nativeInvoices.length === 0
                ? KICK_ONLY_MESSAGE
                : PROCESSING_MESSAGE,
        downloadableCount: nativeInvoices.length,
    };
}
